import React, { useCallback, useRef, useState } from 'react';
import { ChevronsLeftRight } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const BeforeAfterSlider = ({ before, after, alt = '', className = '', style, testId = 'before-after-slider' }) => {
  const { t } = useLanguage();
  const ref = useRef(null);
  const [pos, setPos] = useState(50);
  const dragging = useRef(false);

  const update = useCallback((clientX) => {
    const rect = ref.current?.getBoundingClientRect();
    if (!rect) return;
    const pct = ((clientX - rect.left) / rect.width) * 100;
    setPos(Math.min(100, Math.max(0, pct)));
  }, []);

  const onPointerDown = (e) => {
    dragging.current = true;
    try { ref.current?.setPointerCapture(e.pointerId); } catch { /* unsupported pointer */ }
    update(e.clientX);
  };
  const onPointerMove = (e) => dragging.current && update(e.clientX);
  const onPointerUp = () => { dragging.current = false; };

  return (
    <div
      ref={ref}
      className={`relative select-none overflow-hidden rounded-xl bg-black touch-none cursor-ew-resize ${className}`}
      style={style}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      onPointerCancel={onPointerUp}
      data-testid={testId}
    >
      <img src={after} alt={alt} className="block w-full h-full object-cover" draggable={false} />
      <img
        src={before}
        alt={alt}
        className="absolute inset-0 w-full h-full object-cover"
        style={{ clipPath: `inset(0 ${100 - pos}% 0 0)` }}
        draggable={false}
      />

      <span className="absolute top-2 left-2 md:top-4 md:left-4 px-2 py-0.5 md:px-3 md:py-1 rounded-full bg-black/60 backdrop-blur-sm text-white text-[10px] md:text-xs font-semibold uppercase tracking-[0.15em] md:tracking-[0.2em]" data-testid="before-label">
        {t({ sl: 'Prej', en: 'Before', it: 'Prima' })}
      </span>
      <span className="absolute top-2 right-2 md:top-4 md:right-4 px-2 py-0.5 md:px-3 md:py-1 rounded-full bg-gradient-to-r from-cyan-400 via-fuchsia-500 to-yellow-400 text-black text-[10px] md:text-xs font-semibold uppercase tracking-[0.15em] md:tracking-[0.2em]" data-testid="after-label">
        {t({ sl: 'Potem', en: 'After', it: 'Dopo' })}
      </span>

      <div className="absolute inset-y-0 w-[2px] bg-white shadow-[0_0_12px_rgba(255,255,255,0.8)] pointer-events-none" style={{ left: `${pos}%` }}>
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-9 h-9 md:w-11 md:h-11 rounded-full bg-white text-black flex items-center justify-center shadow-lg">
          <ChevronsLeftRight className="w-5 h-5" />
        </div>
      </div>

      <input
        type="range"
        min="0"
        max="100"
        value={pos}
        onChange={(e) => setPos(Number(e.target.value))}
        aria-label={t({ sl: 'Primerjava prej / potem', en: 'Before / after comparison', it: 'Confronto prima / dopo' })}
        className="absolute inset-0 w-full h-full opacity-0 cursor-ew-resize"
        data-testid="before-after-range"
      />
    </div>
  );
};
